/**
 * A zip file written without a dependency (design systems round 2): the API's design-system export
 * and the console's folder upload write the same format. Each file is stored, or deflated when the
 * caller hands in a deflater (`node:zlib`'s `deflateRawSync` on the API; the browser stores) and
 * it makes the file smaller. Names are UTF-8 (general-purpose flag bit 11). No zip64: a design
 * system is 40 MB at most, far below the format's 4 GB.
 */
let table;
/** CRC-32 (IEEE 802.3), as zip records it. */
export function crc32(bytes) {
    if (!table) {
        table = new Uint32Array(256);
        for (let n = 0; n < 256; n++) {
            let c = n;
            for (let k = 0; k < 8; k++)
                c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
            table[n] = c >>> 0;
        }
    }
    let c = 0xffffffff;
    for (let i = 0; i < bytes.length; i++)
        c = table[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
    return (c ^ 0xffffffff) >>> 0;
}
/** The DOS date and time zip keeps (local time, two-second steps; 1980 at the earliest). */
function dosTime(d) {
    const year = Math.max(1980, d.getFullYear());
    return { time: (d.getHours() << 11) | (d.getMinutes() << 5) | Math.floor(d.getSeconds() / 2), date: ((year - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate() };
}
/** The zip's bytes: every file in order, then the central directory. */
export function writeZip(files, opts = {}) {
    if (files.length > 0xffff)
        throw new Error("a zip holds at most 65,535 files without zip64");
    const enc = new TextEncoder();
    const { time, date } = dosTime(opts.when ?? new Date());
    const locals = [];
    const centrals = [];
    let offset = 0;
    for (const f of files) {
        const name = enc.encode(f.path);
        const crc = crc32(f.bytes);
        let data = f.bytes;
        let method = 0;
        if (opts.deflate && f.bytes.length > 64) {
            const packed = opts.deflate(f.bytes);
            if (packed.length < f.bytes.length) {
                data = packed;
                method = 8;
            }
        }
        if (offset + 30 + name.length + data.length > 0xffffffff)
            throw new Error("the zip would pass 4 GB");
        const local = new Uint8Array(30 + name.length);
        const l = new DataView(local.buffer);
        l.setUint32(0, 0x04034b50, true);
        l.setUint16(4, 20, true);
        l.setUint16(6, 0x0800, true);
        l.setUint16(8, method, true);
        l.setUint16(10, time, true);
        l.setUint16(12, date, true);
        l.setUint32(14, crc, true);
        l.setUint32(18, data.length, true);
        l.setUint32(22, f.bytes.length, true);
        l.setUint16(26, name.length, true);
        l.setUint16(28, 0, true);
        local.set(name, 30);
        const central = new Uint8Array(46 + name.length);
        const c = new DataView(central.buffer);
        c.setUint32(0, 0x02014b50, true);
        c.setUint16(4, 20, true);
        c.setUint16(6, 20, true);
        c.setUint16(8, 0x0800, true);
        c.setUint16(10, method, true);
        c.setUint16(12, time, true);
        c.setUint16(14, date, true);
        c.setUint32(16, crc, true);
        c.setUint32(20, data.length, true);
        c.setUint32(24, f.bytes.length, true);
        c.setUint16(28, name.length, true);
        c.setUint32(42, offset, true);
        central.set(name, 46);
        locals.push(local, data);
        centrals.push(central);
        offset += local.length + data.length;
    }
    const centralSize = centrals.reduce((n, c) => n + c.length, 0);
    const end = new Uint8Array(22);
    const e = new DataView(end.buffer);
    e.setUint32(0, 0x06054b50, true);
    e.setUint16(8, files.length, true);
    e.setUint16(10, files.length, true);
    e.setUint32(12, centralSize, true);
    e.setUint32(16, offset, true);
    const out = new Uint8Array(offset + centralSize + end.length);
    let at = 0;
    for (const part of [...locals, ...centrals, end]) {
        out.set(part, at);
        at += part.length;
    }
    return out;
}
//# sourceMappingURL=zip.js.map